import React from "react";
import {
  Box,
  Checkbox,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import {
  Category as ClassificationIcon,
  TrendingDown as UndervalIcon,
  TrendingUp as OvervalIcon,
} from "@mui/icons-material";
import { HTSCodeWithTooltip } from "@/components/hts-code-with-tooltip";
import { formatCurrencyPrecise } from "./verification-helpers";
import { tableCompactSx, tableContainerSx } from "@/components/table/table-styles";
import SeverityDot from "@/components/severity-dot";
import Pagination from "@/components/paginations/pagination";
import { tokens } from "@/tokens";

const CLASSIFICATION_COLUMNS = [
  { key: "checkbox", label: "", padding: "checkbox" as const },
  { key: "entryNumber", label: "Entry #" },
  { key: "entryDate", label: "Entry Date" },
  { key: "type", label: "Type" },
  { key: "severity", label: "Severity" },
  { key: "issue", label: "Issue" },
  { key: "filedHts", label: "Filed HTS" },
  { key: "correctHts", label: "Correct HTS" },
  { key: "dutyImpact", label: "Duty Impact", align: "right" as const },
  { key: "status", label: "Status" },
];

const getSubTypeChip = (subType?: ClassificationSubType) => {
  switch (subType) {
    case "WRONG_METAL_SPLIT":
      return { label: "Metal Split", bgcolor: "#FEF3C7", color: "#92400E", border: "#FDE68A" };
    case "WRONG_STACKING":
      return { label: "Stacking", bgcolor: "#EDE9FE", color: "#5B21B6", border: "#DDD6FE" };
    case "WRONG_HTS_CODE":
    default:
      return { label: "HTS Code", bgcolor: "#E0E7FF", color: "#4F46E5", border: "#C7D2FE" };
  }
};

interface ClassificationDutyTabProps {
  items: ClassificationDiscrepancyItem[];
  countDocs: number;
  isLoading: boolean;
  isFetching: boolean;
  selectedKeys: Set<string>;
  setSelectedKeys: React.Dispatch<React.SetStateAction<Set<string>>>;
  page: number;
  limit: number;
  handlePageChange: (newPage: number) => void;
  handleLimitChange: (newLimit: number) => void;
  onItemClick?: (item: ClassificationDiscrepancyItem) => void;
}

const getItemKey = (item: ClassificationDiscrepancyItem): string =>
  `${item.auditId}:${item.discrepancyId}`;

const ClassificationDutyTab: React.FC<ClassificationDutyTabProps> = ({
  items,
  countDocs,
  isLoading,
  isFetching,
  selectedKeys,
  setSelectedKeys,
  page,
  limit,
  handlePageChange,
  handleLimitChange,
  onItemClick,
}) => {
  const visibleKeys = items.map(getItemKey);
  const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selectedKeys.has(k));
  const someVisibleSelected = visibleKeys.some((k) => selectedKeys.has(k));

  const handleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(visibleKeys));
    }
  };

  const handleSelectRow = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <>
      {/* Classification Discrepancies Table */}
      <Box sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}>
        <Stack sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          mx: 3,
          mt: 1,
          mb: 2,
          ...tableContainerSx,
          ...tableCompactSx,
        }}>
          {isLoading ? (
            <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: "12px" }} />
          ) : (
            <>
              <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto", position: "relative" }}>
                {isFetching && !isLoading && (
                  <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {CLASSIFICATION_COLUMNS.map((col) => (
                        <TableCell
                          key={col.key}
                          align={col.align || "left"}
                          padding={col.padding || "normal"}
                          sx={{
                            ...(col.key === "checkbox" && { width: 42 }),
                            ...(col.padding === "checkbox" && { padding: "0 0 0 8px !important" }),
                          }}
                        >
                          {col.key === "checkbox" ? (
                            <Checkbox
                              size="small"
                              checked={allVisibleSelected}
                              indeterminate={someVisibleSelected && !allVisibleSelected}
                              onChange={handleSelectAll}
                              sx={{
                                color: tokens.text.subtle,
                                "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: tokens.text.primary },
                              }}
                            />
                          ) : (
                            col.label
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, idx) => {
                      const itemKey = getItemKey(item);
                      const isSelected = selectedKeys.has(itemKey);
                      const reviewStatus = item.reviewStatus;
                      return (
                        <TableRow
                          key={`${item.auditId}-${idx}`}
                          onClick={() => onItemClick?.(item)}
                          sx={{
                            cursor: "pointer",
                            backgroundColor: isSelected ? tokens.bg.muted : tokens.bg.surface,
                            "&:hover": {
                              backgroundColor: isSelected ? tokens.bg.border : tokens.bg.hover,
                            },
                          }}
                        >
                          {/* Checkbox */}
                          <TableCell padding="checkbox" sx={{ pl: 1 }}>
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => handleSelectRow(itemKey)}
                              sx={{
                                color: tokens.text.subtle,
                                "&.Mui-checked": { color: tokens.text.primary },
                              }}
                            />
                          </TableCell>
                          {/* Entry # */}
                          <TableCell sx={{ fontWeight: tokens.fontWeight.medium, fontFamily: "monospace", fontSize: tokens.fontSize.sm }}>
                            {item.entryNumber || "-"}
                          </TableCell>
                          {/* Entry Date */}
                          <TableCell sx={{ color: tokens.text.secondary, whiteSpace: "nowrap" }}>
                            {item.entryDate
                              ? new Date(item.entryDate).toLocaleDateString("en-US", {
                                  month: "short", day: "numeric", year: "numeric",
                                })
                              : "-"}
                          </TableCell>
                          {/* Type */}
                          <TableCell>
                            {(() => {
                              const chip = getSubTypeChip(item.classificationSubType);
                              return (
                                <Chip
                                  label={chip.label}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: tokens.fontSize["2xs"],
                                    fontWeight: tokens.fontWeight.bold,
                                    borderRadius: 0.5,
                                    bgcolor: chip.bgcolor,
                                    color: chip.color,
                                    border: `1px solid ${chip.border}`,
                                  }}
                                />
                              );
                            })()}
                          </TableCell>
                          {/* Severity */}
                          <TableCell>
                            <SeverityDot severity={item.severity?.toLowerCase() as "high" | "medium" | "low"} />
                          </TableCell>
                          {/* Issue — with colored arrow */}
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                              {item.dutyImpactType === "UNDERPAYMENT" ? (
                                <UndervalIcon sx={{ fontSize: 14, color: tokens.status.danger, mt: 0.25, flexShrink: 0 }} />
                              ) : (
                                <OvervalIcon sx={{ fontSize: 14, color: tokens.status.success, mt: 0.25, flexShrink: 0 }} />
                              )}
                              <Tooltip title={`${item.title} — ${item.description}`} arrow placement="top-start">
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography noWrap sx={{ fontWeight: tokens.fontWeight.book, fontSize: tokens.fontSize.base, maxWidth: 200 }}>
                                    {item.title}
                                  </Typography>
                                  <Typography noWrap sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, fontWeight: tokens.fontWeight.book, maxWidth: 200 }}>
                                    {item.description}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          {/* Filed HTS */}
                          <TableCell sx={{ fontFamily: "monospace", fontWeight: tokens.fontWeight.book, whiteSpace: "nowrap" }}>
                            {item.filedHtsCode ? (
                              <HTSCodeWithTooltip htsCode={item.filedHtsCode}>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "inherit",
                                    fontFamily: "inherit",
                                    fontWeight: "inherit",
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  {item.filedHtsCode}
                                </Typography>
                              </HTSCodeWithTooltip>
                            ) : "-"}
                          </TableCell>
                          {/* Correct HTS / Sub-type summary */}
                          <TableCell sx={{ fontFamily: "monospace", fontWeight: tokens.fontWeight.book, whiteSpace: "nowrap" }}>
                            {item.classificationSubType === "WRONG_METAL_SPLIT" ? (
                              <Typography component="span" sx={{ fontSize: "inherit", fontFamily: "inherit", fontWeight: "inherit" }}>
                                {`${Math.round((item.filedMetalPercentage ?? 0) * 100)}%`}
                                <Typography component="span" sx={{ color: tokens.text.muted, mx: 0.5 }}>{" → "}</Typography>
                                {`${Math.round((item.correctMetalPercentage ?? 0) * 100)}%`}
                              </Typography>
                            ) : item.classificationSubType === "WRONG_STACKING" ? (
                              <Typography component="span" sx={{ fontSize: tokens.fontSize.xs, fontFamily: "inherit" }}>
                                {item.stackingIssueType === "MISSING_CODE" ? "Missing" : item.stackingIssueType === "EXTRA_CODE" ? "Extra" : "Wrong"} Ch.99
                              </Typography>
                            ) : item.correctHtsCode ? (
                              <HTSCodeWithTooltip htsCode={item.correctHtsCode}>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "inherit",
                                    fontFamily: "inherit",
                                    fontWeight: "inherit",
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  {item.correctHtsCode}
                                </Typography>
                              </HTSCodeWithTooltip>
                            ) : "-"}
                          </TableCell>
                          {/* Duty Impact */}
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            <Typography
                              sx={{
                                fontWeight: tokens.fontWeight.medium,
                                fontSize: tokens.fontSize.base,
                                color: item.dutyImpactType === "UNDERPAYMENT"
                                  ? tokens.status.danger
                                  : item.dutyImpactType === "OVERPAYMENT"
                                  ? tokens.status.success
                                  : tokens.text.subtle,
                              }}
                            >
                              {(item.dutyDifference ?? 0) > 0
                                ? formatCurrencyPrecise(item.dutyDifference)
                                : "-"}
                            </Typography>
                          </TableCell>
                          {/* Status */}
                          <TableCell>
                            {reviewStatus ? (
                              <Box>
                                <Chip
                                  label={reviewStatus === "confirmed" ? "Confirmed" : "Not an Issue"}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: 18,
                                    fontSize: tokens.fontSize["2xs"],
                                    fontWeight: tokens.fontWeight.book,
                                    borderRadius: 0.5,
                                    bgcolor: 'transparent',
                                    ...(reviewStatus === "confirmed"
                                      ? { color: tokens.status.danger, borderColor: tokens.status.danger }
                                      : { color: tokens.status.success, borderColor: tokens.status.success }),
                                  }}
                                />
                                {item.reviewStatusSetBy && (
                                  <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, mt: 0.25 }}>
                                    by {item.reviewStatusSetBy}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography sx={{ color: tokens.text.subtle, fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.light }}>
                                Pending
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={CLASSIFICATION_COLUMNS.length} sx={{ textAlign: "center", py: 6 }}>
                          <Stack alignItems="center" spacing={2}>
                            <ClassificationIcon sx={{ fontSize: 48, color: tokens.border.default }} />
                            <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                              No HTS classification discrepancies found
                            </Typography>
                            <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted }}>
                              HTS classification issues will appear here once audits identify them
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {countDocs > 0 && (
                <Pagination
                  page={page}
                  limit={limit}
                  total={countDocs}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange}
                />
              )}
            </>
          )}
        </Stack>
      </Box>
    </>
  );
};

export default ClassificationDutyTab;
